import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/core/utils';

interface CardSkeletonProps {
  className?: string;
  headerHeight?: 'sm' | 'md' | 'lg';
  contentRows?: number;
  contentWidths?: ('full' | '3/4' | '1/2' | '1/3' | '1/4')[];
  showHeader?: boolean;
  animated?: boolean;
}

interface SkeletonRowProps {
  width?: 'full' | '3/4' | '1/2' | '1/3' | '1/4';
  height?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const SkeletonRow: React.FC<SkeletonRowProps> = ({ 
  width = 'full', 
  height = 'md',
  className 
}) => {
  const widthClasses = {
    'full': 'w-full',
    '3/4': 'w-3/4',
    '1/2': 'w-1/2',
    '1/3': 'w-1/3',
    '1/4': 'w-1/4'
  };

  const heightClasses = {
    'sm': 'h-3',
    'md': 'h-4',
    'lg': 'h-6',
    'xl': 'h-8'
  };

  return (
    <div className={cn(
      'bg-muted rounded-md',
      widthClasses[width],
      heightClasses[height],
      className
    )} />
  );
};

export const CardSkeleton: React.FC<CardSkeletonProps> = ({
  className,
  headerHeight = 'md',
  contentRows = 3,
  contentWidths = ['full', '3/4', '1/2'],
  showHeader = true,
  animated = true
}) => {
  const headerHeightClasses = {
    'sm': 'h-4',
    'md': 'h-6',
    'lg': 'h-8'
  };

  return (
    <Card className={cn(
      "relative overflow-hidden",
      animated && "animate-pulse",
      className
    )}>
      {showHeader && (
        <CardHeader className="space-y-0 pb-2">
          <CardTitle className="space-y-2">
            <div className={cn(
              'bg-muted rounded-md w-1/3',
              headerHeightClasses[headerHeight]
            )} />
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-3">
        {Array.from({ length: contentRows }).map((_, index) => {
          const width = contentWidths[index % contentWidths.length];
          return (
            <SkeletonRow 
              key={index} 
              width={width}
              height={index === 0 ? 'lg' : 'md'}
            />
          );
        })}
      </CardContent>
    </Card>
  );
};

// Specialized skeleton components for common card types
export const StatCardSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <CardSkeleton
    className={className}
    headerHeight="sm"
    contentRows={2}
    contentWidths={['1/2', '3/4']}
  />
);

export const WeatherCardSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <Card className={cn("col-span-2 relative overflow-hidden animate-pulse", className)}>
    {/* Background gradient */}
    <div className="absolute inset-0 bg-gradient-to-b from-gray-300 via-gray-400 to-gray-500" />
    
    {/* Semi-transparent overlay */}
    <div className="absolute inset-0 bg-black/20 pointer-events-none"></div>
    
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative z-10">
      <div className="h-4 bg-white/20 rounded-md w-20"></div>
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 bg-white/20 rounded"></div>
        <div className="h-4 w-4 bg-white/20 rounded"></div>
      </div>
    </CardHeader>
    <CardContent className="grid grid-cols-2 gap-4 relative z-10">
      {/* Left Column */}
      <div className="space-y-2">
        <div className="h-8 bg-white/20 rounded-md"></div>
        <div className="h-3 bg-white/15 rounded-md w-3/4"></div>
        <div className="h-3 bg-white/15 rounded-md w-1/2"></div>
      </div>
      {/* Right Column */}
      <div className="space-y-2">
        <div className="h-8 bg-white/20 rounded-md w-20"></div>
        <div className="h-3 bg-white/15 rounded-md w-32"></div>
        <div className="h-4 bg-white/15 rounded-md w-28"></div>
      </div>
    </CardContent>
  </Card>
);

export const ChartCardSkeleton: React.FC<{ className?: string }> = ({ className }) => (
  <CardSkeleton
    className={className}
    headerHeight="md"
    contentRows={4}
    contentWidths={['full', 'full', '3/4', '1/2']}
  />
);

export const ListCardSkeleton: React.FC<{ className?: string; items?: number }> = ({ 
  className, 
  items = 3 
}) => (
  <CardSkeleton
    className={className}
    headerHeight="md"
    contentRows={items * 2}
    contentWidths={['3/4', '1/2']}
  />
);

export default CardSkeleton;